// APIs
import { productKinds, productRootSubtypes } from '@iheartjane/taxonomy'
import { LineageOptions, ProductKind, ProductTypeOptions, Options } from './types'
import { SearchFunc } from './redux/types/ui'

export const API_PRODUCTS = '/api/products'
export const API_BRANDS = '/api/product_brands'
export const API_BRAND_SEARCH = '/api/product_brands/search'
export const API_ALIASES = '/api/products/aliases'
export const API_PRODUCT_ALIASES = '/api/product-aliases'
export const API_WHOAMI = '/api/whoami'
export const API_LOGIN = '/api/login'
export const API_LOGOUT = '/api/logout'
export const API_S3_UPLOAD = '/api/s3_upload'
export const API_BRAND_LOGO_S3_UPLOAD = '/api/brand-logo-s3'
export const API_PRODUCT_NOTICES = '/api/products/notices'
export const API_PRODUCT_MERGE = '/api/products/merge'
export const API_POST_ZIP_FILE = '/api/zip-to-s3'
export const API_LOCALIZATIONS = '/api/catalog_product_localizations'

// Paths
export const PATH_ROOT = '/'
export const PATH_PRODUCTS = '/products'
export const PATH_BATCH_LOOKUP = '/batch-lookup'
export const PATH_PRODUCTS_CREATE = '/products/create'
export const PATH_PRODUCT_NOTICES = '/products/notices'
export const PATH_BRAND_THEMES = '/products/brand-themes'
export const PATH_BATCH_PRODUCT_CREATOR = '/products/batch-creator'
export const PATH_BULK_EDITOR = '/products/bulk-editor'
export const PATH_CLUMP_EDITOR = '/products/clump-editor'

export const PATH_PRODUCTS_NEW = '/products/new'
export const PATH_PRODUCTS_EDIT = '/products/edit'
export const PATH_PRODUCTS_ALIAS_REMAP = '/products/:productId/aliases/remap'

export const PATH_FEATURE_FLAGS = '/flags'

export const SWITCH_TO_EDIT = 'Switch to Edit'
export const SWITCH_TO_CREATE = 'Switch to Create'

export const BATCH_REQUEST_DELAY: Record<SearchFunc, number> = {
  sql: 1000,
  algolia: 250,
}

export const PRODUCT_TYPE_OPTIONS: ProductTypeOptions[] = [
  { id: '', key: '', text: '', value: '' },
  { id: 0, key: 'flower', text: 'Flower', value: 'flower' },
  { id: 1, key: 'edible', text: 'Edible', value: 'edible' },
  { id: 2, key: 'extract', text: 'Extract', value: 'extract' },
  { id: 3, key: 'merch', text: 'Merch', value: 'merch' },
  { id: 4, key: 'grow', text: 'Grow', value: 'grow' },
  { id: 5, key: 'tincture', text: 'Tincture', value: 'tincture' },
  { id: 6, key: 'gear', text: 'Gear', value: 'gear' },
  { id: 7, key: 'topical', text: 'Topical', value: 'topical' },
  { id: 8, key: 'pre-roll', text: 'Pre-roll', value: 'pre-roll' },
  { id: 9, key: 'vape', text: 'Vape', value: 'vape' },
]

export const LINEAGE_OPTIONS: LineageOptions[] = [
  { id: 0, key: 'indica', text: 'Indica', value: 'indica' },
  { id: 1, key: 'sativa', text: 'Sativa', value: 'sativa' },
  { id: 2, key: 'hybrid', text: 'Hybrid', value: 'hybrid' },
  { id: 3, key: 'cbd', text: 'CBD', value: 'cbd' },
]

export const QUANTITY_UNIT_OPTIONS: Options[] = [
  { key: '', text: '', value: '' },
  { key: 'g', text: 'Gram (weight)', value: 'g' },
  { key: 'oz', text: 'Ounce (weight)', value: 'oz' },
  { key: 'fl_oz', text: 'Fluid ounce (volume)', value: 'fl_oz' },
  { key: 'ml', text: 'Milliliter (volume)', value: 'ml' },
  { key: 'mg', text: 'Milligram (dosage)', value: 'mg' },
]

export const PRODUCT_SUBTYPE_OPTIONS: { [key in ProductKind]: Options[] } = Object.fromEntries(
  productKinds().map((kind) => [
    kind,
    productRootSubtypes(kind).map((name) => ({ key: name, text: name, value: name })),
  ])
) as { [key in ProductKind]: Options[] }

export const SEARCH_LIMIT_OPTIONS: Options[] = [
  { key: '5', text: '5', value: '5' },
  { key: '20', text: '20', value: '20' },
  { key: '50', text: '50', value: '50' },
  { key: '100', text: '100', value: '100' },
  { key: '250', text: '250', value: '250' },
  { key: '500', text: '500', value: '500' },
  { key: '750', text: '750', value: '750' },
  { key: '1000', text: '1000', value: '1000' },
  { key: '1250', text: '1250', value: '1250' },
  { key: '1500', text: '1500', value: '1500' },
  { key: '1750', text: '1750', value: '1750' },
  { key: '2000', text: '2000', value: '2000' },
]

export const PRODUCT_TYPE_ID_BY_KEY: { [key: string]: any } = {}
PRODUCT_TYPE_OPTIONS.forEach((option) => {
  PRODUCT_TYPE_ID_BY_KEY[option.key] = option.id
})

export const LINEAGE_ID_BY_KEY: { [key: string]: any } = {}
LINEAGE_OPTIONS.forEach((option) => {
  LINEAGE_ID_BY_KEY[option.key] = option.id
})

export const PRODUCT_TYPE_KEY_BY_ID: { [key: number]: ProductKind } = {}
PRODUCT_TYPE_OPTIONS.forEach((option) => {
  if (typeof option.id === 'number' && option.key) {
    PRODUCT_TYPE_KEY_BY_ID[option.id] = option.key
  }
})

export const PRODUCT_TYPE_NAME_BY_ID: { [key: number]: string } = {}
PRODUCT_TYPE_OPTIONS.forEach((option) => {
  if (typeof option.id === 'number') {
    PRODUCT_TYPE_NAME_BY_ID[option.id] = option.text
  }
})

export const LINEAGE_NAME_BY_ID: { [key: number]: string } = {}
LINEAGE_OPTIONS.forEach((option) => {
  if (typeof option.id === 'number') {
    LINEAGE_NAME_BY_ID[option.id] = option.text
  }
})

export const BRAND_MODE_OPTIONS = [
  {
    key: 'phonetic',
    value: 'phonetic',
    icon: 'play circle outline',
    text: 'Phonetic matching',
  },
  {
    key: 'strict',
    value: 'strict',
    icon: 'target',
    text: 'Strict matching',
  },
  {
    key: 'none',
    value: 'none',
    icon: 'ban',
    text: 'No brand',
  },
]

// Sets the header background color depending on environment
export const HEADER_BACKGROUND_COLOR = process.env.REACT_APP_ENV !== 'prod' ? '#0C645D' : '#5723E7' // Dark Cornflower : Jane Purple

export interface AbbreviatedProduct {
  id: number
  name: string
  brand: string
  brand_subtype: string
  lineage: string
  product_type: number
  image_urls: string[]
}

export interface AbbreviatedProductContainer {
  [key: number]: AbbreviatedProduct
}
